import React, { FC, useState } from 'react';
import { Button, Divider, Popconfirm, Select } from '@retail-core/rds';
import './SelectionSidebar.less';
import { Article } from '../../models/sizing-data';
import { SizingGroup } from '../../models/sizing-group';
import { PlusOutlined } from '@ant-design/icons';
import './ArticleFieldSelector.less';
import useDeepCompareEffect from 'use-deep-compare-effect';

interface ArticleFieldSelectorProps {
  sizingGroups: Array<SizingGroup>;
  selectedArticles: Array<Article>;
  updateArticles?: (articleIds: Array<string>, modifier: Record<string, string | null>) => void;
  loading?: boolean;
  hasManuallyEditedArticles?: boolean;
  hasBoxEnabledArticles?: boolean;
  field: 'sizeStructure' | 'sizingNote' | 'silo';
  placeholder?: string;
  nullValueDisplay?: string;
  nullOptionDisplay?: string;
  commonOptions?: Array<string>;
  confirmBeforeApply?: boolean;
}
export const ArticleFieldSelector: FC<ArticleFieldSelectorProps> = ({
  sizingGroups,
  selectedArticles,
  loading,
  updateArticles,
  hasManuallyEditedArticles,
  hasBoxEnabledArticles,
  field,
  placeholder,
  nullValueDisplay,
  nullOptionDisplay,
  commonOptions,
  confirmBeforeApply,
}) => {
  const uniqueFieldValues = new Set(selectedArticles.map((article) => article?.[field]));
  const selectedValue = uniqueFieldValues.size > 1 ? 'Mixed' : selectedArticles[0]?.[field] || undefined;
  const [selectedFieldValue, setSelectedFieldValue] = useState<string | undefined>(selectedValue);
  const [popConfirmVisible, setPopConfirmVisible] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const existingFieldValueOptions = sizingGroups.map((sg) => sg[field]).filter((x) => !!x);
  const uniqueFieldValueOptions = [...new Set(existingFieldValueOptions)];

  const normalizeFieldValue = (value?: string): string | null | undefined => {
    if (value === '') {
      return null;
    }
    return value;
  };

  const handleChange = (value?: string) => {
    const normalizedValue = normalizeFieldValue(value);
    setSelectedFieldValue(value);
    if (
      !popConfirmVisible &&
      (confirmBeforeApply || hasBoxEnabledArticles || hasManuallyEditedArticles || uniqueFieldValues.size > 1)
    ) {
      setPopConfirmVisible(true);
      return;
    }
    if (
      uniqueFieldValues.size === 1 &&
      (uniqueFieldValues.has(normalizedValue) || (uniqueFieldValues.has(null) && normalizedValue == null))
    ) {
      return;
    }
    !loading &&
      value !== undefined &&
      updateArticles?.(
        selectedArticles.map((article) => article.referenceId),
        { [field]: normalizedValue ?? null }
      );
  };
  const confirm = () => {
    setPopConfirmVisible(false);
    handleChange(selectedFieldValue);
  };

  const cancel = () => {
    setPopConfirmVisible(false);
    setSelectedFieldValue(selectedValue);
  };
  const popconfirmMessage = hasBoxEnabledArticles
    ? 'Box restrictions recommendations for the selected article(s) will be lost when moved to a new group'
    : `${nullValueDisplay?.replace('Add', 'Apply this')} to all selected articles, overwriting existing values?`;
  useDeepCompareEffect(() => {
    setSelectedFieldValue(selectedValue);
  }, [selectedArticles]);
  return (
    <Popconfirm
      overlayStyle={{ width: '426px' }}
      visible={popConfirmVisible}
      title={popconfirmMessage}
      placement="bottom"
      onConfirm={confirm}
      onCancel={cancel}
      okText="Apply"
      cancelText="Cancel"
      data-testid={`${field}-selector-confirm`}
      arrowPointAtCenter
    >
      <Select
        data-testid={`${field}-selector`}
        style={{ display: 'block' }}
        showSearch
        placeholder={nullValueDisplay}
        value={selectedFieldValue}
        loading={loading}
        onSelect={(value?: string) => {
          setSearchValue('');
          handleChange(value);
        }}
        onSearch={setSearchValue}
        searchValue={searchValue}
        filterOption={(input, option) =>
          String(option?.value ?? '')
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        bordered={false}
        size="small"
        dropdownRender={(menu) => {
          return (
            <div data-testid={`${field}-selector-dropdown`}>
              {menu}
              {searchValue && !uniqueFieldValueOptions.includes(searchValue) ? (
                <>
                  <Divider className="article-field-selector-dropdown-divider" />
                  <Button
                    type="text"
                    className="article-field-selector-dropdown-create-button"
                    block
                    icon={
                      // @ts-ignore
                      <PlusOutlined />
                    }
                    onClick={() => {
                      const value = searchValue;
                      setSearchValue('');
                      handleChange(value);
                    }}
                  >
                    Create group for '{searchValue}'
                  </Button>
                </>
              ) : null}
            </div>
          );
        }}
      >
        <Select.Option key={'no-value'} value={''} data-testid={`${field}-selector-`}>
          <div>{nullOptionDisplay}</div>
        </Select.Option>
        {uniqueFieldValueOptions.length ? (
          <Select.OptGroup label="Move to another group">
            {uniqueFieldValueOptions.map((fieldValue, index) => (
              <Select.Option
                key={`existing-${index}-${fieldValue}`}
                value={fieldValue || ''}
                data-testid={`${field}-selector-${fieldValue}`}
              >
                <div>{fieldValue}</div>
              </Select.Option>
            ))}
          </Select.OptGroup>
        ) : null}
        {commonOptions && (
          <Select.OptGroup label="Create common sizing group">
            {commonOptions.map((option, index) => (
              <Select.Option key={`common-${index}-${option}`} value={option}>
                {option}
              </Select.Option>
            ))}
          </Select.OptGroup>
        )}
      </Select>
    </Popconfirm>
  );
};
